import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useAuth } from './auth'
import { loadCachedSession, saveCachedSession } from './sessionCache'
import { restGet } from './rest'
import type { Tenant } from './types'

interface TenantState {
  loading: boolean
  tenant: Tenant | null
  // True ONLY when the server has confirmed the tenant doesn't exist for
  // this user. Network failures don't set this.
  tenantMissing: boolean
  isPaid: boolean
  refresh: () => Promise<void>
}

const TenantCtx = createContext<TenantState | null>(null)

async function retry<T>(fn: () => Promise<T>, opts: { tries: number; baseMs: number }): Promise<T> {
  let lastErr: any
  for (let i = 0; i < opts.tries; i++) {
    try { return await fn() }
    catch (e) {
      lastErr = e
      const wait = opts.baseMs * Math.pow(2, i)
      await new Promise(r => setTimeout(r, wait))
    }
  }
  throw lastErr
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth()
  const [loading, setLoading] = useState(true)
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [tenantMissing, setTenantMissing] = useState(false)

  async function fetchTenant(tenantId: string): Promise<Tenant | 'missing'> {
    // Raw REST (not supabase-js): the supabase-js client wedges in long-
    // lived browser sessions and we've watched this exact query hang for
    // tens of seconds while a direct fetch returns the same row in ~120ms.
    const data = await restGet<Tenant | null>('tenants', {
      select: '*',
      filters: [`id=eq.${tenantId}`],
      single: true,
      timeoutMs: 8000,
      label: 'tenant fetch',
    })
    if (data == null) return 'missing'
    return data
  }

  async function load() {
    if (!user?.tenant_id) {
      // We can't fetch — either no user yet OR user has no tenant.
      // Don't FLIP tenantMissing here; auth.tsx tracks user-side missing.
      setTenant(null); setLoading(false); return
    }

    // Optimistic from cache so we don't hold the UI behind a query.
    const authUserId = session?.user?.id || null
    const cached = loadCachedSession(authUserId)
    if (cached?.tenant && cached.tenant.id === user.tenant_id) {
      setTenant(cached.tenant); setLoading(false)
    } else {
      // No cached tenant — synthesise a MINIMAL one so App.tsx renders
      // immediately. Real tenant fetch below fills in name/logo/brand/plan.
      // Optimistically assume 'paid' so existing paying customers don't see
      // "Upgrade" prompts on every full reload while the real fetch is in
      // flight. If they're actually on trial, the real fetch (which always
      // wins) will demote them back to 'trial' once it lands — far less
      // jarring than the inverse.
      setTenant({
        id: user.tenant_id,
        name: '',
        slug: '',
        logo_url: null,
        brand_colour: '#16A34A',
        signature_url: null,
        signature_name: null,
        signature_role: null,
        plan: 'paid',
        stripe_customer_id: null,
        stripe_subscription_id: null,
        billing_email: null,
        created_at: '',
        updated_at: '',
      })
      setLoading(false)
    }

    try {
      const result = await retry(() => fetchTenant(user.tenant_id), { tries: 6, baseMs: 500 })
      if (result === 'missing') {
        setTenantMissing(true); setTenant(null)
      } else {
        setTenantMissing(false); setTenant(result)
        if (authUserId) saveCachedSession(authUserId, user, result)
      }
    } catch (e) {
      console.warn('[tenant] load failed after retries', e)
      // Keep the synthetic/cached tenant; never demote to null on a network blip.
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() /* eslint-disable-next-line */ }, [user?.tenant_id])

  const isPaid = tenant?.plan === 'paid'

  return (
    <TenantCtx.Provider value={{ loading, tenant, tenantMissing, isPaid, refresh: load }}>
      {children}
    </TenantCtx.Provider>
  )
}

export function useTenant() {
  const ctx = useContext(TenantCtx)
  if (!ctx) throw new Error('useTenant must be used inside TenantProvider')
  return ctx
}
